import React, { useState, useEffect } from 'react';
import { TbCategory2 } from "react-icons/tb";
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Play,
  Award,
  ChevronRight,
  PlayCircle,
  Eye,
  Package,
  Brain
} from 'lucide-react';
import { useCategories, useLocalStorage } from '../../hooks';
import { Card, LoadingSpinner, Button, Modal } from '../../components/common';
import { ProductType } from '../../types';

/**
 * Presets de header: 1 color por card (Brinsa) con FADE a blanco.
 * Texto siempre oscuro para asegurar legibilidad.
 * Escala a cualquier número de categorías (hash por id).
 */
const HEADER_PRESETS = [
  // Azul Brinsa -> Blanco
  'bg-[linear-gradient(180deg,rgba(0,22,136,0.18)_0%,rgba(0,22,136,0.08)_50%,white_100%)]',
  // Verde Brinsa -> Blanco
  'bg-[linear-gradient(180deg,rgba(0,168,1,0.20)_0%,rgba(0,168,1,0.10)_50%,white_100%)]',
  // Celeste Brinsa -> Blanco
  'bg-[linear-gradient(180deg,rgba(53,193,215,0.22)_0%,rgba(53,193,215,0.10)_50%,white_100%)]',
  // Morado Brinsa -> Blanco
  'bg-[linear-gradient(180deg,rgba(179,43,166,0.20)_0%,rgba(179,43,166,0.10)_50%,white_100%)]',
  // Amarillo Brinsa -> Blanco
  'bg-[linear-gradient(180deg,rgba(248,167,3,0.22)_0%,rgba(248,167,3,0.10)_50%,white_100%)]',
  // Turquesa Brinsa -> Blanco
  'bg-[linear-gradient(180deg,rgba(23,240,215,0.22)_0%,rgba(23,240,215,0.10)_50%,white_100%)]',
];

function pickHeaderPreset(key?: string) {
  if (!key) return HEADER_PRESETS[0];
  let sum = 0;
  for (let i = 0; i < key.length; i++) sum = (sum + key.charCodeAt(i)) % 997;
  return HEADER_PRESETS[sum % HEADER_PRESETS.length];
}

const HomePage: React.FC = () => {
  const { data: categoriesData, isLoading } = useCategories();
  const [showTutorial, setShowTutorial] = useState(false);
  const [hasSeenTutorial, setHasSeenTutorial] = useLocalStorage('hasSeenHomeTutorial', false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = categoriesData?.categories || [];

  useEffect(() => {
    if (!hasSeenTutorial && categories.length > 0) {
      setShowTutorial(true);
    }
  }, [hasSeenTutorial, categories.length]);

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    setHasSeenTutorial(true);
  };

  const getCategoryIcon = (type: ProductType) => {
    switch (type) {
      case ProductType.BLANQUEADOR:
        return '/assets/icons/01_blanqueadores.png';
      case ProductType.LIMPIADORES:
        return '/assets/icons/02_limpiadores.png';
      case ProductType.LAVALOZAS:
        return '/assets/icons/03_lavalozas.png';
      case ProductType.DETERGENTES:
        return '/assets/icons/04_detergentes.png';
      case ProductType.DESMANCHADORES:
        return '/assets/icons/05_desmanchadores.png';
      case ProductType.BLANCOX:
        return '/assets/icons/06_blancoxPRO.png';
      case ProductType.ALIMENTOS:
        return '/assets/icons/07_alimentos.png';
      default:
        return '📦';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Tutorial Modal */}
      <TutorialModal isOpen={showTutorial} onComplete={handleTutorialComplete} />

      {/* Modal de categoría seleccionada */}
      <CategoryModal categoryId={selectedCategory} onClose={() => setSelectedCategory(null)} />

      {/* Encabezado */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h1 className="text-3xl font-bold text-brinsa-azul mb-4">
          Categorías de Productos
        </h1>
        <p className="text-lg text-brinsa-azulOscuro/80 max-w-2xl mx-auto">
          Explora nuestras categorías de productos y aprende sobre cada una de ellas
          a través de videos, realidad aumentada, portafolios y pruebas interactivas.
        </p>
      </motion.div>

      {/* Grid de categorías */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
      >
        {categories.map((category, index) => {
         const headerBg = HEADER_PRESETS[index % HEADER_PRESETS.length];
          const icon = getCategoryIcon(category.type);

          return (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="group"
            >
              <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow duration-300 border border-slate-200 rounded-2xl">
                {/* HEADER: 1 color con fade */}
                <div className={`${headerBg} p-6 relative overflow-hidden rounded-b-none`}>
                  {/* círculo decorativo */}
                  <div className="absolute top-0 right-3 w-12 h-12 rounded-full grid place-items-center">
                    {typeof icon === 'string' && icon.startsWith('/') ? (
                      <img src={icon} alt={category.name} className="w-18 h-18 object-contain" />
                    ) : (
                      <span className="text-xl">{icon}</span>
                    )}
                  </div>

                  {/* Texto oscuro para contraste */}
                  <h3 className="text-xl font-bold text-brinsa-azul mb-1">
                    {category.name}
                  </h3>
                  <p className="text-sm text-brinsa-azulOscuro/85">
                    {category.description}
                  </p>
                </div>

                {/* BODY */}
                <div className="p-6 space-y-4">
                  {/* Chips de acceso con colores Brinsa */}
                  <div className="grid grid-cols-2 gap-3">
                    <Link
                      to={`/category/${category.id}/video`}
                      className="flex items-center justify-center p-3  text-brinsa-rojo rounded-lg hover:bg-brinsa-rojo/15 transition-colors border border-slate-200"
                    >
                      <PlayCircle className="w-5 h-5 mr-2" />
                      <span className="text-sm font-medium">Videos</span>
                    </Link>

                    <Link
                      to={`/category/${category.id}/ar`}
                      className="flex items-center justify-center p-3  text-brinsa-morado rounded-lg hover:bg-brinsa-morado/15 transition-colors border border-slate-200"
                    >
                      <Eye className="w-5 h-5 mr-2" />
                      <span className="text-sm font-medium">Realidad AR</span>
                    </Link>

                    <Link
                      to={`/category/${category.id}/portfolio`}
                      className="flex items-center justify-center p-3 text-brinsa-celeste rounded-lg hover:bg-brinsa-celeste/15 transition-colors border border-slate-200"
                    >
                      <Package className="w-5 h-5 mr-2" />
                      <span className="text-sm font-medium">Portafolio</span>
                    </Link>

                    <Link
                      to={`/category/${category.id}/test`}
                      className="flex items-center justify-center p-3  text-brinsa-verde rounded-lg hover:bg-brinsa-verde/15 transition-colors border border-slate-200"
                    >
                      <Brain className="w-5 h-5 mr-2" />
                      <span className="text-sm font-medium">Pruebas</span>
                    </Link>
                  </div>

                  {/* CTA principal */}
                  <Button
                    className="w-full"
                    onClick={() => setSelectedCategory(category.id)}
                    rightIcon={<ChevronRight className="w-4 h-4" />}
                  >
                    Explorar {category.name}
                  </Button>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Sección de ayuda */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="rounded-lg p-8 text-center border border-slate-200 bg-brinsa-celeste/10"
      >
        <h2 className="text-2xl font-bold text-brinsa-azul mb-4">
          ¿Necesitas ayuda?
        </h2>
        <p className="text-brinsa-azulOscuro/85 mb-6 max-w-2xl mx-auto">
          Si es tu primera vez en la plataforma, te recomendamos ver el tutorial
          para conocer todas las funcionalidades disponibles.
        </p>
        <Button
          variant="outline"
          onClick={() => setShowTutorial(true)}
          leftIcon={<Play className="w-4 h-4" />}
        >
          Ver Tutorial
        </Button>
      </motion.div>
    </div>
  );
};

/* -------- Modal de tutorial -------- */

interface TutorialModalProps {
  isOpen: boolean;
  onComplete: () => void;
}

const TutorialModal: React.FC<TutorialModalProps> = ({ isOpen, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const tutorialSteps = [
    {
      title: '¡Te damos la bienvenida al Campus Virtus Brinsa!',
      description: 'Para que tengas la mejor experiencia de aprendizaje ten en cuenta las siguientes recomendaciones.',
      image: '/assets/icons/home.png',
    },
    {
      title: '1. Lugar',
      description: 'Asegúrate de estar en un lugar silencioso y con buena conexión a internet.',
      image: '/assets/icons/wifi.png',
    },
    {
      title: '2. Tiempo',
      description: 'Asegúrate de disponer con 15 minutos para ver el material completo.',
      image: '/assets/icons/time.png',
    },
    {
      title: '3. Audifonos',
      description: 'Es preferible que uses audífonos para evitar interferencias por el sonido ambiente',
      image: '/assets/icons/headphone.png',
    },
    {
      title: '¡Comienza a Aprender!',
      description: 'Ahora estás listo para explorar las categorías y comenzar tu experiencia de aprendizaje con Brinsa.',
      image: '/assets/icons/home_blue.png',
    },
  ];

  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
      setCurrentStep(0);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onComplete} size="lg">
      <div className="text-center p-6">
        {typeof tutorialSteps[currentStep].image === 'string' &&
        tutorialSteps[currentStep].image.startsWith('/') ? (
          <img
            src={tutorialSteps[currentStep].image}
            alt={tutorialSteps[currentStep].title}
            className="mx-auto w-20 h-20 mb-4"
          />
        ) : (
          <div className="text-6xl mb-4">{tutorialSteps[currentStep].image}</div>
        )}

        <h3 className="text-xl font-bold text-brinsa-azul mb-4">
          {tutorialSteps[currentStep].title}
        </h3>

        <p className="text-brinsa-azulOscuro/85 mb-8">
          {tutorialSteps[currentStep].description}
        </p>

        <div className="flex justify-center space-x-2 mb-8">
          {tutorialSteps.map((_, index) => (
            <div
              key={index}
              className={`w-3 h-3 rounded-full ${index === currentStep ? 'bg-brinsa-azul' : 'bg-slate-300'}`}
            />
          ))}
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={prevStep} disabled={currentStep === 0}>
            Anterior
          </Button>
          <Button onClick={nextStep}>
            {currentStep === tutorialSteps.length - 1 ? 'Comenzar' : 'Siguiente'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

/* -------- Modal de categoría -------- */

interface CategoryModalProps {
  categoryId: string | null;
  onClose: () => void;
}

const CategoryModal: React.FC<CategoryModalProps> = ({ categoryId, onClose }) => {
  return (
    <Modal isOpen={!!categoryId} onClose={onClose} title="Tutorial de Categoría" size="lg">
      <div className="space-y-6">
        <div className="text-center">
          {/* <div className="text-4xl mb-4">TbCategory2</div> */}
          <TbCategory2  
            className="mx-auto mb-4 w-10 h-10 text-brinsa-azul"
            aria-hidden="true"
          />
          <h3 className="text-lg font-semibold text-brinsa-azul mb-2">
            ¿Qué puedes hacer en esta categoría?
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-brinsa-rojo/10 p-4 rounded-lg border border-slate-200">
            <PlayCircle className="w-8 h-8 text-brinsa-rojo mb-2" />
            <h4 className="font-medium text-brinsa-azul mb-1">Videos</h4>
            <p className="text-sm text-brinsa-azulOscuro/85">
              Mira videos educativos sobre los productos de esta categoría.
            </p>
          </div>

          <div className="bg-brinsa-morado/10 p-4 rounded-lg border border-slate-200">
            <Eye className="w-8 h-8 text-brinsa-morado mb-2" />
            <h4 className="font-medium text-brinsa-azul mb-1">Realidad Aumentada</h4>
            <p className="text-sm text-brinsa-azulOscuro/85">
              Experimenta con modelos 3D usando realidad aumentada.
            </p>
          </div>

          <div className="bg-brinsa-celeste/10 p-4 rounded-lg border border-slate-200">
            <Package className="w-8 h-8 text-brinsa-celeste mb-2" />
            <h4 className="font-medium text-brinsa-azul mb-1">Portafolio</h4>
            <p className="text-sm text-brinsa-azulOscuro/85">
              Explora todos los productos disponibles en diferentes presentaciones.
            </p>
          </div>

          <div className="bg-brinsa-verde/10 p-4 rounded-lg border border-slate-200">
            <Brain className="w-8 h-8 text-brinsa-verde mb-2" />
            <h4 className="font-medium text-brinsa-azul mb-1">Pruebas</h4>
            <p className="text-sm text-brinsa-azulOscuro/85">
              Evalúa tus conocimientos con pruebas interactivas.
            </p>
          </div>
        </div>

        <div className="bg-brinsa-amarillo/10 p-4 rounded-lg border border-brinsa-amarillo/30">
          <div className="flex items-center">
            <Award className="w-6 h-6 text-brinsa-amarillo mr-3" />
            <div>
              <h4 className="font-medium text-brinsa-azul">¡Obtén tu certificado!</h4>
              <p className="text-sm text-brinsa-azulOscuro/85">
                Completa todas las actividades para recibir tu certificado de finalización.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <Link to={`/category/${categoryId}`}>
            <Button onClick={onClose}>Comenzar Ahora</Button>
          </Link>
        </div>
      </div>
    </Modal>
  );
};

export default HomePage;
